import { useEffect, useMemo, useRef, useState } from "react";
import { previewSegment, type SegmentPreviewResponse } from "src/services/SegmentAPIs";
import type { SegmentPreview, SegmentRules } from "./CreateSegment.Constants";
import { buildSegmentConditions } from "./CreateSegment.Utils";

const PREVIEW_DEBOUNCE_MS = 400;

export type SegmentPreviewStatus = "idle" | "loading" | "ready" | "error";

interface PreviewState {
  preview: SegmentPreview | null;
  status: SegmentPreviewStatus;
}

const IDLE_STATE: PreviewState = { preview: null, status: "idle" };

const toPreview = (response: SegmentPreviewResponse): SegmentPreview => ({
  people: response.total,
  email: response.email.count,
  whatsapp: response.whatsapp.count,
  steps: [...response.steps].sort((a, b) => a.index - b.index).map(step => step.count),
});

/** Running count for the rules being built: debounced, and only the latest request is kept. */
export const useSegmentPreview = (rules: SegmentRules, enabled: boolean) => {
  const conditions = useMemo(
    () => (enabled ? buildSegmentConditions(rules) : null),
    [enabled, rules]
  );
  const requestKey = conditions ? JSON.stringify(conditions) : null;
  const conditionsRef = useRef(conditions);
  conditionsRef.current = conditions;
  const [state, setState] = useState<PreviewState>(IDLE_STATE);

  useEffect(() => {
    const latest = conditionsRef.current;
    if (!requestKey || !latest) {
      setState(IDLE_STATE);
      return undefined;
    }

    const controller = new AbortController();
    setState(current => ({ ...current, status: "loading" }));
    const timer = window.setTimeout(() => {
      previewSegment(latest, controller.signal)
        .then(response => setState({ preview: toPreview(response), status: "ready" }))
        .catch(() => {
          if (!controller.signal.aborted) {
            setState({ preview: null, status: "error" });
          }
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [requestKey]);

  return state;
};
